import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  User, Mail, CheckCircle, AlertCircle, Lock, BookOpen,
  Award, Bookmark, ArrowRight, ExternalLink, Trash2, Shield
} from 'lucide-react';
import { useReaderAuth } from '../context/ReaderAuthContext';
import { useCourseAccess } from '../hooks/useCourseAccess';
import { useModuleCompletion } from '../hooks/useModuleCompletion';
import { useModuleProgress } from '../hooks/useModuleProgress';
import { useSavedArticles } from '../hooks/useSavedArticles';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const NAVY  = '#071B3A';
const GOLD  = '#C99316';
const IVORY = '#FAF8F3';

const MODULE_META = [
  { number: 1, title: 'Self-Anchoring', subtitle: 'Identity, Integrity & the Inner Compass', free: true },
  { number: 2, title: 'Perspective-Shifting', subtitle: 'Seeing Clearly Across Cultures', free: false },
  { number: 3, title: 'Adaptive Communication', subtitle: 'Expressing Clearly Without Losing Your Voice', free: false },
  { number: 4, title: 'Relational Integration', subtitle: 'Building Trust & Cohesion Across Cultures', free: false },
];

const ReaderDashboard = () => {
  const { user, token, logout } = useReaderAuth();
  const { hasAccess, purchasedModules, hasModuleAccess, isLoading: accessLoading } = useCourseAccess();
  const { completions, isModuleCompleted } = useModuleCompletion();
  const { getModuleProgress } = useModuleProgress();
  const { savedArticles, unsaveArticle } = useSavedArticles();

  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyMsg, setVerifyMsg]     = useState('');

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: IVORY }}>
        <div className="text-center">
          <p className="font-serif text-2xl mb-4" style={{ color: NAVY }}>Please sign in</p>
          <Link to="/books" className="underline text-sm" style={{ color: GOLD }}>Go to sign in</Link>
        </div>
      </div>
    );
  }

  const isVerified = user.email_verified || user.verified;

  const handleSendVerification = async () => {
    setVerifyLoading(true);
    setVerifyMsg('');
    try {
      const r = await fetch(`${API_URL}/api/reader/send-verification`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      const d = await r.json();
      setVerifyMsg(d.message || 'Email sent');
    } catch {
      setVerifyMsg('Failed to send. Please try again.');
    } finally {
      setVerifyLoading(false);
    }
  };

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: IVORY }}>
      <Helmet><meta name="robots" content="noindex, nofollow, noarchive" /></Helmet>

      {/* ── Header ─────────────────────────────────────────── */}
      <div className="pt-16 pb-10 px-6 md:px-12" style={{ backgroundColor: NAVY }}>
        <div className="max-w-5xl mx-auto">
          <div className="flex items-start justify-between gap-6">
            <div>
              <p className="text-[11px] tracking-[0.25em] uppercase font-sans mb-3" style={{ color: `${GOLD}80` }}>
                Reader Portal
              </p>
              <h1 className="font-serif text-4xl font-light" style={{ color: IVORY }}>
                Welcome, {user.first_name || user.name || 'Reader'}
              </h1>
              <p className="mt-2 font-sans text-sm flex items-center gap-2" style={{ color: `${IVORY}40` }}>
                <Mail size={13} />
                {user.email}
                {isVerified
                  ? <span className="text-green-400 flex items-center gap-1"><CheckCircle size={12} /> Verified</span>
                  : <span className="text-amber-400 flex items-center gap-1"><AlertCircle size={12} /> Unverified</span>
                }
              </p>
            </div>
            <button
              onClick={logout}
              className="text-[11px] uppercase tracking-[0.15em] font-sans px-4 py-2 border transition-colors"
              style={{ borderColor: `${IVORY}15`, color: `${IVORY}33` }}
              data-testid="dashboard-logout-btn"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 md:px-12 pt-8 space-y-10">

        {/* ── Email Verification Banner ──────────────────────── */}
        {!isVerified && (
          <div
            className="flex flex-col sm:flex-row sm:items-center gap-4 p-5 rounded"
            style={{ backgroundColor: '#FEF3C7', border: '1px solid #F59E0B33' }}
            data-testid="email-verify-banner"
          >
            <div className="flex items-center gap-3 flex-1">
              <AlertCircle className="text-amber-500 flex-shrink-0" size={20} />
              <div>
                <p className="font-sans font-medium text-amber-800 text-sm">Please verify your email address</p>
                <p className="font-sans text-amber-700 text-xs mt-0.5">Verify to unlock all article features and receive course updates.</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {verifyMsg
                ? <span className="text-xs text-amber-700">{verifyMsg}</span>
                : (
                  <button
                    onClick={handleSendVerification}
                    disabled={verifyLoading}
                    className="text-[11px] uppercase tracking-[0.15em] font-sans px-5 py-2 font-medium transition-colors disabled:opacity-50"
                    style={{ backgroundColor: GOLD, color: NAVY }}
                    data-testid="send-verification-btn"
                  >
                    {verifyLoading ? 'Sending...' : 'Send verification email'}
                  </button>
                )
              }
            </div>
          </div>
        )}

        {/* ── Module Progress ────────────────────────────────── */}
        <section data-testid="module-progress-section">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-serif text-2xl font-light" style={{ color: NAVY }}>Module Progress</h2>
            {!hasAccess && !accessLoading && (
              <Link
                to="/courses"
                className="text-[11px] uppercase tracking-[0.15em] font-sans flex items-center gap-1"
                style={{ color: GOLD }}
              >
                Enrol <ArrowRight size={12} />
              </Link>
            )}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {MODULE_META.map(mod => {
              const accessible = mod.free || hasModuleAccess(mod.number);
              const completed  = isModuleCompleted(mod.number);
              const purchased  = purchasedModules.includes(`module-${mod.number}`);
              const sectionProg = getModuleProgress(mod.number);
              const inProgress = accessible && !completed && sectionProg && sectionProg.last_section > 0;

              return (
                <div
                  key={mod.number}
                  className="relative rounded overflow-hidden"
                  style={{
                    backgroundColor: '#fff',
                    border: `1px solid ${completed ? GOLD + '66' : '#E2E8F0'}`
                  }}
                  data-testid={`module-card-${mod.number}`}
                >
                  {/* Status strip */}
                  <div
                    className="h-1"
                    style={{
                      backgroundColor: completed ? GOLD : inProgress ? '#3B82F6' : accessible ? '#22C55E' : '#E2E8F0'
                    }}
                  />
                  <div className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <span
                        className="text-[10px] tracking-[0.2em] uppercase font-sans font-medium"
                        style={{ color: `${NAVY}4D` }}
                      >
                        Module {mod.number}
                      </span>
                      {completed
                        ? <Award size={16} style={{ color: GOLD }} />
                        : inProgress
                          ? <span className="text-[9px] uppercase tracking-[0.15em] font-sans px-1.5 py-0.5 rounded-sm" style={{ backgroundColor: 'rgba(59,130,246,0.08)', color: '#3B82F6' }}>
                              {sectionProg.last_section}/{sectionProg.total_sections}
                            </span>
                        : accessible
                          ? <CheckCircle size={16} className="text-green-500" />
                          : <Lock size={16} style={{ color: `${NAVY}33` }} />
                      }
                    </div>
                    <p className="font-serif text-base font-light leading-tight mb-1" style={{ color: NAVY }}>
                      {mod.title}
                    </p>
                    <p className="text-xs font-sans leading-snug mb-4" style={{ color: `${NAVY}4D` }}>
                      {mod.subtitle}
                    </p>

                    {accessible ? (
                      <Link
                        to={`/module-${mod.number}`}
                        className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-sans font-medium"
                        style={{ color: GOLD }}
                      >
                        {completed ? 'Review' : 'Continue'} <ArrowRight size={11} />
                      </Link>
                    ) : (
                      <Link
                        to="/courses"
                        className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-sans"
                        style={{ color: `${NAVY}33` }}
                      >
                        Unlock <ExternalLink size={11} />
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* ── Saved Articles ─────────────────────────────────── */}
        <section data-testid="saved-articles-section">
          <div className="flex items-center gap-2 mb-5">
            <Bookmark size={16} style={{ color: GOLD }} />
            <h2 className="font-serif text-2xl font-light" style={{ color: NAVY }}>Saved Articles</h2>
            <span
              className="ml-1 text-xs font-sans px-2 py-0.5 rounded-full"
              style={{ backgroundColor: `${GOLD}1A`, color: GOLD }}
            >
              {savedArticles.length}
            </span>
          </div>

          {savedArticles.length === 0 ? (
            <div
              className="py-12 text-center rounded"
              style={{ backgroundColor: '#fff', border: '1px solid #E2E8F0' }}
            >
              <Bookmark size={28} className="mx-auto mb-3" style={{ color: `${NAVY}1A` }} />
              <p className="font-serif text-lg font-light mb-1" style={{ color: `${NAVY}40` }}>No saved articles yet</p>
              <p className="text-sm font-sans mb-4" style={{ color: `${NAVY}33` }}>
                Click the bookmark icon on any article to save it here.
              </p>
              <Link
                to="/articles"
                className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-sans font-medium"
                style={{ color: GOLD }}
              >
                Browse Articles <ArrowRight size={11} />
              </Link>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {savedArticles.map(art => (
                <div
                  key={art.id}
                  className="rounded overflow-hidden group"
                  style={{ backgroundColor: '#fff', border: '1px solid #E2E8F0' }}
                  data-testid="saved-article-card"
                >
                  {art.article_image && (
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={art.article_image}
                        alt={art.article_title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                  <div className="p-4">
                    {art.article_category && (
                      <span
                        className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-wide font-sans font-medium rounded-full mb-2"
                        style={{ backgroundColor: `${GOLD}1A`, color: GOLD }}
                      >
                        {art.article_category}
                      </span>
                    )}
                    <h3 className="font-serif text-base font-light leading-snug mb-2 line-clamp-2" style={{ color: NAVY }}>
                      {art.article_title}
                    </h3>
                    <div className="flex items-center justify-between mt-3">
                      <Link
                        to={`/articles/${art.article_slug}`}
                        className="text-[11px] uppercase tracking-[0.15em] font-sans flex items-center gap-1"
                        style={{ color: GOLD }}
                      >
                        Read <ArrowRight size={11} />
                      </Link>
                      <button
                        onClick={() => unsaveArticle(art.article_id)}
                        className="p-1.5 rounded transition-colors hover:bg-red-50"
                        title="Remove from saved"
                        data-testid="unsave-article-btn"
                      >
                        <Trash2 size={13} className="text-slate-400 hover:text-red-500" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* ── Ebook / Book Link ──────────────────────────────── */}
        <section>
          <h2 className="font-serif text-2xl font-light mb-5" style={{ color: NAVY }}>Reading</h2>
          <div
            className="flex flex-col sm:flex-row sm:items-center gap-5 p-6 rounded"
            style={{ backgroundColor: NAVY }}
          >
            <BookOpen size={28} style={{ color: `${GOLD}80` }} className="flex-shrink-0" />
            <div className="flex-1">
              <p className="font-serif text-base font-light mb-1" style={{ color: IVORY }}>
                Cultural Fluency Without Compromise
              </p>
              <p className="text-xs font-sans" style={{ color: `${IVORY}40` }}>
                Dr Promise Monday · Promond Publishing
              </p>
            </div>
            <Link
              to="/books/cultural-fluency-without-compromise/read"
              className="inline-flex items-center gap-2 px-5 py-3 text-[11px] uppercase tracking-[0.15em] font-sans font-medium flex-shrink-0"
              style={{ backgroundColor: GOLD, color: NAVY }}
              data-testid="continue-reading-btn"
            >
              Continue Reading <ArrowRight size={13} />
            </Link>
          </div>
        </section>

        {/* ── Certificates ──────────────────────────────────── */}
        {completions.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-5">
              <Shield size={16} style={{ color: GOLD }} />
              <h2 className="font-serif text-2xl font-light" style={{ color: NAVY }}>Certificates</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {completions.map(c => (
                <div
                  key={c.module_number}
                  className="flex items-center gap-4 p-5 rounded"
                  style={{ backgroundColor: '#fff', border: `1px solid ${GOLD}33` }}
                >
                  <Award size={24} style={{ color: GOLD }} />
                  <div className="flex-1">
                    <p className="font-serif text-sm font-light" style={{ color: NAVY }}>Module {c.module_number} Complete</p>
                    <p className="text-xs font-sans mt-0.5" style={{ color: `${NAVY}40` }}>
                      {new Date(c.completed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </p>
                  </div>
                  <Link
                    to={`/module-${c.module_number}`}
                    className="text-[10px] uppercase tracking-[0.15em] font-sans"
                    style={{ color: GOLD }}
                  >
                    View
                  </Link>
                </div>
              ))}
            </div>
          </section>
        )}

      </div>
    </div>
  );
};

export default ReaderDashboard;
